import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Check, Lock, Plus, Star, Pencil, Trash2, Eye, EyeOff, Sliders } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuCheckboxItem,
} from './ui/dropdown-menu';
import { VIEW_ICONS, DEFAULT_VIEW_TYPES, viewLabel, tabName, InlineRename } from './ViewTabsBar';
import { useViewFieldConfig } from '../hooks/useViewFieldConfig';
import { useIsMobile } from '../hooks/useIsMobile';
import { cn } from '../lib/utils';

/**
 * A group of the menu ("Add view", "Display"): a submenu at the side on a computer; on a phone
 * the side has no room (it opened off the screen, task ddc7af8d), so the group opens in place,
 * under its item, in the menu that scrolls.
 */
function MenuGroup({ icon: Icon, label, phone, open, onToggle, width, children }) {
  if (!phone) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger className="text-[13px]">
          <Icon className="w-4 h-4 text-muted-foreground" />{label}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className={cn(width, 'max-h-80 overflow-y-auto')}>{children}</DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }
  return (
    <>
      <DropdownMenuItem onSelect={(e) => { e.preventDefault(); onToggle(); }} className="text-[13px]" aria-expanded={open}>
        <Icon className="text-muted-foreground" /><span className="flex-1">{label}</span>
        {open ? <ChevronDown className="!size-3.5 text-muted-foreground" /> : <ChevronRight className="!size-3.5 text-muted-foreground" />}
      </DropdownMenuItem>
      {open && <div className="pl-4">{children}</div>}
    </>
  );
}

/**
 * A page's saved views as one button: the view shown now, and a menu to switch to
 * another, add one, or set the shown one as default, rename, hide or delete it. The
 * same views and actions as ViewTabsBar (both take what useViewTabs returns), for
 * pages where a row of tabs would compete with the page's own switch (Tasks:
 * List | Calendar, a project's Tasks · Docs · Chats).
 */
export default function ViewMenu({
  tabs,
  activeView,
  hiddenCatalog = [],
  isLocked = () => false,
  actions,
  loading = false,
  availableViewTypes = DEFAULT_VIEW_TYPES,
  customFieldDefs = [],
  canEdit = true,
  className,
  ...rest
}) {
  const { t } = useTranslation();
  const [renaming, setRenaming] = useState(null);
  // Rename from the menu: the name field keeps the focus the closing menu would take back.
  const renameNext = useRef(false);
  const phone = useIsMobile();
  const [group, setGroup] = useState(null);  // the group open in place on a phone: 'add' | 'display'
  const { allFields, toggleField } = useViewFieldConfig(activeView, actions, customFieldDefs);
  const button = 'h-9 md:h-8 px-2.5 flex items-center gap-1.5 rounded-md border border-border text-xs transition-colors';

  if (loading || !activeView) {
    return (
      <div className={cn(button, 'w-24', className)} aria-hidden {...rest}>
        <div className="w-3.5 h-3.5 rounded-sm bg-muted-foreground/15 animate-pulse" />
        <div className="flex-1 h-3 rounded bg-muted-foreground/15 animate-pulse" />
      </div>
    );
  }

  if (renaming) {
    return (
      <div className={className} {...rest}>
        <InlineRename
          value={renaming.value}
          onChange={(value) => setRenaming((r) => ({ ...r, value }))}
          onCommit={() => {
            const v = renaming.value.trim();
            if (v && v !== tabName(t, activeView)) actions.renameView(renaming.id, v);
            setRenaming(null);
          }}
          onCancel={() => setRenaming(null)}
        />
      </div>
    );
  }

  const Icon = VIEW_ICONS[activeView.type];
  const fieldsShown = activeView.type !== 'table' && activeView.type !== 'agenda';
  const canRemove = tabs.length > 1 && !activeView.pinned;

  return (
    <DropdownMenu onOpenChange={(o) => { if (!o) setGroup(null); }}>
      <DropdownMenuTrigger asChild>
        <button type="button" title={t('common.view')} data-testid="view-menu"
          className={cn(button, 'text-foreground hover:bg-accent/60 max-w-[180px]', className)} {...rest}>
          {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />}
          <span className="truncate">{tabName(t, activeView)}</span>
          <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56"
        onCloseAutoFocus={(e) => { if (renameNext.current) { e.preventDefault(); renameNext.current = false; } }}>
        {tabs.map((view) => {
          const ViewIcon = VIEW_ICONS[view.type];
          return (
            <DropdownMenuItem key={view.id} disabled={view.locked} onSelect={() => actions.setActive(view.id)}
              title={view.locked ? t('ui_views.tabs.upgrade_required', { name: viewLabel(t, view.type) }) : undefined}
              data-testid="view-menu-item" className="text-[13px]">
              {ViewIcon && <ViewIcon className="text-muted-foreground" />}
              <span className="flex-1 truncate">{tabName(t, view)}</span>
              {view.isDefault && !view.locked && <Star className="!size-3 text-amber-500 fill-amber-500" />}
              {view.locked && <Lock className="!size-3 text-amber-500" />}
              {activeView.id === view.id && <Check className="!size-3.5" />}
            </DropdownMenuItem>
          );
        })}
        {canEdit && (
          <>
            <DropdownMenuSeparator />
            <MenuGroup icon={Plus} label={t('view_switcher.add_view')} phone={phone} width="w-44"
              open={group === 'add'} onToggle={() => setGroup(group === 'add' ? null : 'add')}>
                {availableViewTypes.map((type) => {
                  const TypeIcon = VIEW_ICONS[type];
                  return (
                    <DropdownMenuItem key={type} onSelect={() => actions.addView(type)} className="text-[13px]">
                      <TypeIcon className="text-muted-foreground" />
                      <span className="flex-1">{viewLabel(t, type)}</span>
                      {isLocked(type) && <Lock className="!size-3 text-amber-500" />}
                    </DropdownMenuItem>
                  );
                })}
            </MenuGroup>
            {fieldsShown && (
              <MenuGroup icon={Sliders} label={t('view_fields.display')} phone={phone} width="w-56"
                open={group === 'display'} onToggle={() => setGroup(group === 'display' ? null : 'display')}>
                  {allFields.map((f) => (
                    <DropdownMenuCheckboxItem key={f.key} checked={f.visible} onSelect={(e) => e.preventDefault()}
                      onCheckedChange={() => toggleField(f.key)} className="text-[13px]">
                      <span className="truncate">{f.isCustom ? f.label : t(f.i18n)}</span>
                    </DropdownMenuCheckboxItem>
                  ))}
              </MenuGroup>
            )}
            <DropdownMenuItem disabled={activeView.isDefault} onSelect={() => actions.setDefault(activeView.id)} className="text-[13px]">
              <Star className="text-muted-foreground" />
              {activeView.isDefault ? t('view_tabs.default_set') : t('view_tabs.set_default')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => { renameNext.current = true; setRenaming({ id: activeView.id, value: tabName(t, activeView) }); }} className="text-[13px]">
              <Pencil className="text-muted-foreground" />{t('view_tabs.rename')}
            </DropdownMenuItem>
            {canRemove && (
              <DropdownMenuItem onSelect={() => actions.hide(activeView.id)} className="text-[13px]">
                <EyeOff className="text-muted-foreground" />{t('view_tabs.hide')}
              </DropdownMenuItem>
            )}
            {hiddenCatalog.map((view) => (
              <DropdownMenuItem key={view.id} onSelect={() => actions.unhide(view.id)} className="text-[13px]">
                <Eye className="text-muted-foreground" />
                <span className="truncate">{t('view_tabs.unhide', { name: tabName(t, view) })}</span>
              </DropdownMenuItem>
            ))}
            {canRemove && (
              <DropdownMenuItem onSelect={() => actions.removeView(activeView.id)}
                className="text-[13px] text-red-500 focus:text-red-500 focus:bg-red-500/10">
                <Trash2 />{t('view_tabs.delete')}
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
